import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from './components/ProgressIndicator';
import DateTravelerStep from './components/DateTravelerStep';
import AddOnsStep from './components/AddOnsStep';
import TravelerInfoStep from './components/TravelerInfoStep';
import PaymentStep from './components/PaymentStep';

const BookingFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    selectedDate: '',
    selectedTime: '',
    adults: 2,
    children: 0,
    infants: 0,
    addOns: [],
    travelers: [],
    contactInfo: {
      email: '',
      phone: '',
      emergencyContact: '',
      emergencyPhone: ''
    },
    specialRequests: '',
    createAccount: false,
    paymentMethod: '',
    totalAmount: 0,
    bookingId: '',
    status: 'pending'
  });

  const steps = [
    {
      id: 1,
      title: 'Date & Travelers',
      subtitle: 'Select dates and group size'
    },
    {
      id: 2,
      title: 'Add-ons',
      subtitle: 'Enhance your experience'
    },
    {
      id: 3,
      title: 'Traveler Info',
      subtitle: 'Enter traveler details'
    },
    {
      id: 4,
      title: 'Payment',
      subtitle: 'Complete your booking'
    }
  ];

  // Initialize booking data from URL params or previous selections
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tourId = searchParams?.get('tourId');
    const date = searchParams?.get('date');
    const adults = searchParams?.get('adults');
    const children = searchParams?.get('children');

    if (tourId || date || adults || children) {
      setBookingData(prev => ({
        ...prev,
        tourId: tourId || prev?.tourId,
        selectedDate: date || prev?.selectedDate,
        adults: adults ? parseInt(adults) : prev?.adults,
        children: children ? parseInt(children) : prev?.children
      }));
    }
  }, [location?.search]);

  // Save booking data to localStorage for persistence
  useEffect(() => {
    localStorage.setItem('bookingData', JSON.stringify(bookingData));
  }, [bookingData]);

  // Load saved booking data on mount
  useEffect(() => {
    const savedData = localStorage.getItem('bookingData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed?.status !== 'confirmed') {
          setBookingData(parsed);
        }
      } catch (error) {
        console.error('Error loading saved booking data:', error);
      }
    }
  }, []);

  const handleNextStep = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (stepNumber) => {
    // Allow navigation to previous steps only
    if (stepNumber < currentStep) {
      setCurrentStep(stepNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <DateTravelerStep
            onNext={handleNextStep}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 2:
        return (
          <AddOnsStep
            onNext={handleNextStep}
            onBack={handlePreviousStep}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 3:
        return (
          <TravelerInfoStep
            onNext={handleNextStep}
            onBack={handlePreviousStep}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 4:
        return (
          <PaymentStep
            onBack={handlePreviousStep}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      default:
        return null;
    }
  };

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      if (currentStep > 1) {
        event?.preventDefault();
        handlePreviousStep();
      } else {
        navigate('/tour-detail');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentStep, navigate]);

  // Update page title based on current step
  useEffect(() => {
    const stepTitle = steps?.[currentStep - 1]?.title || 'Booking';
    document.title = `${stepTitle} - TourEase Booking`;
  }, [currentStep, steps]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator
        currentStep={currentStep}
        totalSteps={steps?.length}
        steps={steps}
        onStepClick={handleStepClick}
      />
      <main className="pb-16">
        {renderCurrentStep()}
      </main>
      {/* Mobile Step Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 lg:hidden">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Step {currentStep} of {steps?.length}
          </div>
          <div className="text-sm font-medium text-foreground">
            {steps?.[currentStep - 1]?.title}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;